import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import "./RegisterToken.css";
import { api, ENDPOINTS } from "../../services/apiConfig";

const RegisterToken = () => {

  const [code, setCode] = useState(["", "", "", ""]);
  const [status, setStatus] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(30);

  const navigate = useNavigate();

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setInterval(() => {
        setResendCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiration = payload.exp * 1000;
      return Date.now() > expiration;
    } catch (error) {
      return true;
    }
  };

  const getAuthToken = () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      toast.error("Authentication token not found. Please log in again.");
      navigate("/login");
      return null;
    }
    if (isTokenExpired(token)) {
      toast.error("Your session has expired. Please log in again.");
      localStorage.removeItem("authToken");
      navigate("/login");
      return null;
    }
    return token;
  };

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (/^\d$/.test(value) || value === "") {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      if (value && index < 3) {
        document.getElementById(`digit-${index + 1}`).focus();
      }

      if (index === 3 && value) {
        verifyCode(newCode.join(""));
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && code[index] === "" && index > 0) {
      document.getElementById(`digit-${index - 1}`).focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("Text").slice(0, 4).split("");
    const newCode = code.map((digit, i) => pasteData[i] || "");
    setCode(newCode);

    if (pasteData.length === 4) {
      verifyCode(pasteData.join(""));
    }
  };

  const verifyCode = async (fullCode) => {
    if (fullCode.length < 4) {
      setStatus("Please enter the full OTP.");
      return;
    }

    const token = getAuthToken();
    if (!token) return;

    try {
      setStatus("Verifying...");
      const data = await api.vendor.post(ENDPOINTS.vendor.verifyregistered,
        { otp: fullCode },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success(data.message);
      setStatus(data.message);
      navigate("/login");
    } catch (error) {
      const errorMessage = error.data?.message || "Verification failed";
      toast.error(errorMessage);
      setStatus(errorMessage);
      setCode(["", "", "", ""]);
    }
  };

  const resendCode = async () => {
    if (isResending || resendCountdown > 0) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      setIsResending(true);
      const response = await api.vendor.post(ENDPOINTS.vendor.resendotp,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      toast.success("Verification code resent successfully");
      setResendCountdown(30);
      setCode(["", "", "", ""]);
      setStatus(null);
    } catch (error) {
      const errorMessage = error?.data?.message || "Failed to resend code";
      toast.error(errorMessage);
      
      // Handle specific error cases
      if (error.data?.status === 401) {
        localStorage.removeItem("authToken");
        navigate("/login");
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="code-entry-wrapper">
      <ToastContainer />
      <div className="code-entry-container">
        <img src="assets/genie.png" alt="Genie Logo" className="form-logo" />
        <h2>Enter the 4-digit Verification Code to Proceed</h2>
        <div className="code-entry-inputs" onPaste={handlePaste}>
          {code.map((digit, index) => (
            <input
              key={index}
              id={`digit-${index}`}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className="code-input"
              autoComplete="off"
            />
          ))}
        </div>
        {status && <p className={`status-message ${status.includes("success") ? "success" : "error"}`}>{status}</p>}
        <div className="resend-section">
          <p className="resend-text">Didn't receive the code?</p>
          <button
            className="resend-code-button"
            onClick={resendCode}
            disabled={isResending || resendCountdown > 0}
          >
            {isResending 
              ? "Resending..." 
              : resendCountdown > 0 
                ? `Resend code in ${resendCountdown}s` 
                : "Resend code"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterToken;